import { useState, useEffect } from 'react';
import { ReportTemplate, ReportConfig, DEFAULT_TEMPLATE } from '../types/reportConfig';
import { templatesApi, reportConfigsApi } from '../services/reportApi';
import { message } from 'antd';

const TEMPLATES_KEY = 'report_templates';
const CONFIGS_KEY = 'report_configs';

// Detectar si estamos en desarrollo
const isDevelopment = import.meta.env.DEV;

export const useReportConfig = () => {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [configs, setConfigs] = useState<ReportConfig[]>([]);
  const [loading, setLoading] = useState(true);

  // Cargar al montar
  useEffect(() => {
    loadTemplates();
    loadConfigs();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      if (isDevelopment) {
        // Modo desarrollo: usar localStorage
        const saved = localStorage.getItem(TEMPLATES_KEY);
        if (saved) {
          setTemplates(JSON.parse(saved));
        } else {
          setTemplates([DEFAULT_TEMPLATE]);
          localStorage.setItem(TEMPLATES_KEY, JSON.stringify([DEFAULT_TEMPLATE]));
        }
      } else {
        // Modo producción: usar API
        const data = await templatesApi.list();
        setTemplates(data.length > 0 ? data : [DEFAULT_TEMPLATE]);
      }
    } catch (e) {
      console.error('Error al cargar templates:', e);
      setTemplates([DEFAULT_TEMPLATE]);
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    try {
      if (isDevelopment) {
        // Modo desarrollo: usar localStorage
        const saved = localStorage.getItem(CONFIGS_KEY);
        if (saved) {
          setConfigs(JSON.parse(saved));
        }
      } else {
        // Modo producción: usar API
        const data = await reportConfigsApi.list();
        setConfigs(data);
      }
    } catch (e) {
      console.error('Error al cargar configuraciones:', e);
    }
  };

  // TEMPLATES
  const saveTemplate = async (template: ReportTemplate) => {
    try {
      if (isDevelopment) {
        // Modo desarrollo: guardar en localStorage
        const updated = [...templates.filter(t => t.id !== template.id), template];
        setTemplates(updated);
        localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated));
        message.success('Template guardado');
      } else {
        // Modo producción: guardar en API
        const isNew = !templates.find(t => t.id === template.id);
        
        if (isNew) {
          const newId = await templatesApi.create(template);
          const newTemplate = { ...template, id: newId };
          setTemplates([...templates, newTemplate]);
          message.success('Template creado exitosamente');
        } else {
          await templatesApi.update(template);
          setTemplates(templates.map(t => t.id === template.id ? template : t));
          message.success('Template actualizado exitosamente');
        }
        
        await loadTemplates();
      }
    } catch (e: any) {
      console.error('Error al guardar template:', e);
      message.error(e.message || 'Error al guardar template');
      throw e;
    }
  };

  const deleteTemplate = async (id: string) => {
    if (id === 'default' || id === '1') {
      message.warning('No puedes eliminar el template por defecto');
      return;
    }
    
    try {
      if (isDevelopment) {
        // Modo desarrollo: eliminar de localStorage
        const updated = templates.filter(t => t.id !== id);
        setTemplates(updated);
        localStorage.setItem(TEMPLATES_KEY, JSON.stringify(updated));
        message.success('Template eliminado');
      } else {
        // Modo producción: eliminar de API
        await templatesApi.delete(id);
        setTemplates(templates.filter(t => t.id !== id));
        message.success('Template eliminado exitosamente');
      }
    } catch (e: any) {
      console.error('Error al eliminar template:', e);
      message.error(e.message || 'Error al eliminar template');
      throw e;
    }
  };

  const getTemplate = (id: string): ReportTemplate | undefined => {
    return templates.find(t => t.id === id);
  };

  // CONFIGS
  const saveConfig = async (config: ReportConfig) => {
    try {
      if (isDevelopment) {
        // Modo desarrollo: guardar en localStorage
        const updated = [...configs.filter(c => c.id !== config.id), config];
        setConfigs(updated);
        localStorage.setItem(CONFIGS_KEY, JSON.stringify(updated));
        message.success('Configuración guardada');
      } else {
        // Modo producción: guardar en API
        const isNew = !configs.find(c => c.id === config.id);
        
        if (isNew) {
          const newId = await reportConfigsApi.create(config);
          const newConfig = { ...config, id: newId };
          setConfigs([...configs, newConfig]);
          message.success('Configuración creada exitosamente');
        } else {
          message.info('Actualización de configuraciones pendiente de implementar');
        }
        
        await loadConfigs();
      }
    } catch (e: any) {
      console.error('Error al guardar configuración:', e);
      message.error(e.message || 'Error al guardar configuración');
      throw e;
    }
  };

  const deleteConfig = async (id: string) => {
    try {
      if (isDevelopment) {
        // Modo desarrollo: eliminar de localStorage
        const updated = configs.filter(c => c.id !== id);
        setConfigs(updated);
        localStorage.setItem(CONFIGS_KEY, JSON.stringify(updated));
        message.success('Configuración eliminada');
      } else {
        // Modo producción: pendiente de implementar
        message.info('Eliminación de configuraciones pendiente de implementar');
      }
    } catch (e: any) {
      console.error('Error al eliminar configuración:', e);
      message.error(e.message || 'Error al eliminar configuración');
      throw e;
    }
  };

  const toggleConfigActive = async (id: string) => {
    try {
      if (isDevelopment) {
        // Modo desarrollo: actualizar en localStorage
        const updated = configs.map(c =>
          c.id === id ? { ...c, active: !c.active } : c
        );
        setConfigs(updated);
        localStorage.setItem(CONFIGS_KEY, JSON.stringify(updated));
        const config = updated.find(c => c.id === id);
        message.success(`Configuración ${config?.active ? 'activada' : 'desactivada'}`);
      } else {
        // Modo producción: usar API
        const newActive = await reportConfigsApi.toggleActive(id);
        setConfigs(configs.map(c =>
          c.id === id ? { ...c, active: newActive } : c
        ));
        message.success(`Configuración ${newActive ? 'activada' : 'desactivada'}`);
      }
    } catch (e: any) {
      console.error('Error al cambiar estado:', e);
      message.error(e.message || 'Error al cambiar estado');
      throw e;
    }
  };

  const getConfig = (id: string): ReportConfig | undefined => {
    return configs.find(c => c.id === id);
  };

  const updateLastSent = (id: string) => {
    // Esto lo maneja el backend automáticamente
    const updated = configs.map(c =>
      c.id === id ? { ...c, lastSent: new Date().toISOString() } : c
    );
    setConfigs(updated);
  };

  return {
    // State
    loading,
    // Templates
    templates,
    saveTemplate,
    deleteTemplate,
    getTemplate,
    // Configs
    configs,
    saveConfig,
    deleteConfig,
    toggleConfigActive,
    getConfig,
    updateLastSent,
  };
};
